import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Avatar } from './Charts';
import { logout as apiLogout } from '../api/api';
import logo from '../assets/logo.png';

const ICONS = {
  dashboard: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  ),
  add: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8" />
      <path d="M8 12h8" />
    </svg>
  ),
  profile: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
    </svg>
  ),
  admin: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
    </svg>
  ),
  logout: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  ),
  guests: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 21c0-3.9 2.9-6.5 6.5-6.5s6.5 2.6 6.5 6.5" />
      <path d="M17 4.5c1.5.4 2.5 1.7 2.5 3.2s-1 2.8-2.5 3.2" />
      <path d="M19 14.3c1.9.6 3.3 2.3 3.3 4.7" />
    </svg>
  ),
  statement: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M8 8h8" />
      <path d="M8 12h8" />
      <path d="M8 16h5" />
    </svg>
  ),
  chevron: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
};

const ADMIN_SECTIONS = [
  { to: '/admin/resumen', label: 'Resumen' },
  { to: '/admin/usuarios', label: 'Usuarios' },
  { to: '/admin/referencias', label: 'Referencias' },
  { to: '/admin/eventos', label: 'Eventos' },
  { to: '/admin/asistencia', label: 'Asistencia' },
  { to: '/admin/invitados', label: 'Invitados' },
  { to: '/admin/auditoria', label: 'Auditoría' },
];

function SidebarLink({ to, icon, children, active, onNavigate }) {
  return (
    <Link to={to} className={`sidebar-link${active ? ' active' : ''}`} onClick={onNavigate}>
      <span className="sidebar-link-icon">{ICONS[icon]}</span>
      {children}
    </Link>
  );
}

function Sidebar({ profile, mobileOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const inAdmin = location.pathname.startsWith('/admin');
  const [adminOpen, setAdminOpen] = useState(inAdmin);

  // Si navegas a /admin/... desde otro lado (o al revés), el submenú se sincroniza solo.
  useEffect(() => { if (inAdmin) setAdminOpen(true); }, [inAdmin]);

  const handleLogout = async (e) => {
    e.preventDefault();
    try { await apiLogout(); } catch (err) { /* no-op: igual mandamos al login */ }
    navigate('/');
    window.location.reload();
  };

  if (!profile) return null;

  return (
    <>
      {mobileOpen && <div className="sidebar-backdrop" onClick={onClose} />}
      <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
        <Link to="/dashboard" className="sidebar-brand" onClick={onClose}>
          <img src={logo} alt="PYMES Networking HUB" className="sidebar-brand-logo" />
        </Link>

        <nav className="sidebar-nav">
          <SidebarLink to="/dashboard" icon="dashboard" active={location.pathname === '/dashboard'} onNavigate={onClose}>Dashboard</SidebarLink>
          {profile.status === 'active' && (
            <SidebarLink to="/nueva-referida" icon="add" active={location.pathname === '/nueva-referida'} onNavigate={onClose}>Nueva Referencia</SidebarLink>
          )}
          {profile.status === 'active' && (
            <SidebarLink to="/invitados" icon="guests" active={location.pathname === '/invitados'} onNavigate={onClose}>Invitados</SidebarLink>
          )}
          {profile.status === 'active' && !['admin', 'owner'].includes(profile.role) && (
            <SidebarLink to="/estado-cuenta" icon="statement" active={location.pathname === '/estado-cuenta'} onNavigate={onClose}>Estado de Cuenta</SidebarLink>
          )}
          <SidebarLink to="/mi-perfil" icon="profile" active={location.pathname === '/mi-perfil'} onNavigate={onClose}>Mi Perfil</SidebarLink>

          {['admin', 'owner'].includes(profile.role) && (
            <div className="sidebar-group">
              <button type="button" className={`sidebar-link sidebar-group-toggle${inAdmin ? ' active' : ''}`} onClick={() => setAdminOpen(o => !o)}>
                <span className="sidebar-link-icon">{ICONS.admin}</span>
                Admin
                <span className={`sidebar-group-chevron${adminOpen ? ' open' : ''}`}>{ICONS.chevron}</span>
              </button>
              {adminOpen && (
                <div className="sidebar-submenu">
                  {ADMIN_SECTIONS.map(s => (
                    <Link
                      key={s.to}
                      to={s.to}
                      className={`sidebar-sublink${location.pathname === s.to ? ' active' : ''}`}
                      onClick={onClose}
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-profile-row">
            <Avatar src={profile.avatar_url} seed={profile.name} size={30} />
            <div className="sidebar-profile-text">
              <div className="sidebar-profile-name">{profile.name}</div>
              <div className="sidebar-email">{profile.email}</div>
            </div>
          </div>
          <a href="/logout" className="sidebar-logout" onClick={handleLogout}>
            {ICONS.logout} Cerrar sesión
          </a>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
